/**
 * VTT Parser/Serializer — standalone helper for WebVTT subtitle files.
 *
 * Raw VTT is the master format. This helper parses raw VTT into an editable
 * structured document and serializes it back **losslessly** for the pieces the
 * structured editor does not touch:
 *   - per-cue settings (positioning after the `-->` line: line/position/align/...)
 *   - cue identifiers
 *   - non-cue blocks (WEBVTT header extras, STYLE, REGION, NOTE comments)
 *
 * The structured editor only ever edits a cue's start/end/text; everything else
 * rides along untouched. `validateVtt` guards saves while letting visual
 * formatting pass through.
 */

let _cueKeyCounter = 0;
/** Generate a unique internal key for a cue (not serialized). */
export function nextCueKey() {
  return `cue-${++_cueKeyCounter}`;
}

/**
 * Parse a timestamp string (HH:MM:SS.mmm or MM:SS.mmm) into seconds.
 * Returns 0 for unparseable input (safe default during typing).
 */
export function parseTimestamp(ts) {
  if (!ts) return 0;
  const cleaned = ts.trim();
  // Support both HH:MM:SS.mmm and MM:SS.mmm
  const parts = cleaned.split(':');
  let result = 0;
  if (parts.length === 3) {
    const [h, m, rest] = parts;
    const [s, ms] = (rest || '0').split('.');
    result =
      parseInt(h, 10) * 3600 +
      parseInt(m, 10) * 60 +
      parseInt(s, 10) +
      (ms ? parseInt(ms.padEnd(3, '0'), 10) / 1000 : 0);
  } else if (parts.length === 2) {
    const [m, rest] = parts;
    const [s, ms] = (rest || '0').split('.');
    result =
      parseInt(m, 10) * 60 +
      parseInt(s, 10) +
      (ms ? parseInt(ms.padEnd(3, '0'), 10) / 1000 : 0);
  }
  return isNaN(result) ? 0 : result;
}

/**
 * Format seconds into VTT timestamp (HH:MM:SS.mmm).
 */
export function formatTimestamp(seconds) {
  if (seconds < 0) seconds = 0;
  let ms = Math.round(seconds * 1000);
  let h = Math.floor(ms / 3600000);
  ms -= h * 3600000;
  let m = Math.floor(ms / 60000);
  ms -= m * 60000;
  let s = Math.floor(ms / 1000);
  ms -= s * 1000;
  return (
    String(h).padStart(2, '0') +
    ':' +
    String(m).padStart(2, '0') +
    ':' +
    String(s).padStart(2, '0') +
    '.' +
    String(ms).padStart(3, '0')
  );
}

/**
 * True if `ts` is a well-formed WebVTT timestamp ([HH:]MM:SS.mmm) with
 * in-range minute/second fields (00–59). Used by validation only —
 * parseTimestamp stays lenient for live typing.
 */
export function isValidTimestamp(ts) {
  return /^(\d+:)?[0-5]\d:[0-5]\d\.\d{3}$/.test((ts || '').trim());
}

/**
 * A single VTT cue.
 * @typedef {Object} VTTCue
 * @property {string} id - Cue identifier (may be empty)
 * @property {number} startTime - Start time in seconds
 * @property {number} endTime - End time in seconds
 * @property {string} settings - Opaque cue settings after the end timestamp
 *                               (e.g. "line:90% position:50% align:start"), may be ''
 * @property {string} text - Cue text content (inline tags preserved verbatim)
 */

/**
 * A parsed VTT document preserving everything the structured editor can't edit.
 * @typedef {Object} VTTDocument
 * @property {string} header - The leading `WEBVTT...` block, verbatim
 * @property {VTTCue[]} cues - Editable cues in order
 * @property {Array<{beforeIndex: number, text: string}>} extras - Opaque non-cue
 *   blocks (STYLE/REGION/NOTE), each re-emitted before `cues[beforeIndex]`
 *   (`beforeIndex === cues.length` means trailing).
 */

// Parse one block's lines (already known to contain a `-->` line) into a cue.
function parseCueBlock(lines) {
  const tsIdx = lines.findIndex((l) => l.includes('-->'));
  if (tsIdx === -1) return null;

  // Cue ID is any line before the timestamp line
  const id = tsIdx > 0 ? lines.slice(0, tsIdx).join('\n').trim() : '';

  const tsParts = lines[tsIdx].split('-->');
  if (tsParts.length !== 2) return null;

  const startTime = parseTimestamp(tsParts[0]);

  // Right side = end timestamp token + opaque settings remainder
  const right = tsParts[1].trim();
  const spaceIdx = right.search(/\s/);
  const endToken = spaceIdx === -1 ? right : right.slice(0, spaceIdx);
  const settings = spaceIdx === -1 ? '' : right.slice(spaceIdx + 1).trim();
  const endTime = parseTimestamp(endToken);

  const text = lines.slice(tsIdx + 1).join('\n');

  return { _key: nextCueKey(), id, startTime, endTime, settings, text };
}

/**
 * Parse VTT text into a lossless document (header + editable cues + opaque extras).
 * @param {string} vtt - Raw VTT text
 * @returns {VTTDocument}
 */
export function parseVttDocument(vtt) {
  if (!vtt || typeof vtt !== 'string') {
    return { header: 'WEBVTT', cues: [], extras: [] };
  }

  const clean = vtt.replace(/^\uFEFF/, '');
  // Blocks separated by one or more blank lines
  const blocks = clean.split(/\n\s*\n/);

  const cues = [];
  const extras = [];
  let header = null;

  for (const block of blocks) {
    const trimmed = block.replace(/\s+$/, '');
    if (trimmed.trim() === '') continue;

    const lines = trimmed.split('\n');
    const hasArrow = lines.some((l) => l.includes('-->'));

    if (hasArrow) {
      const cue = parseCueBlock(lines);
      if (cue) cues.push(cue);
      else extras.push({ beforeIndex: cues.length, text: trimmed });
    } else if (header === null && /^WEBVTT/.test(trimmed.trim())) {
      // First non-cue block that starts with WEBVTT is the header
      header = trimmed;
    } else {
      // STYLE / REGION / NOTE / stray non-cue content — preserved opaquely
      extras.push({ beforeIndex: cues.length, text: trimmed });
    }
  }

  if (header === null) header = 'WEBVTT';
  return { header, cues, extras };
}

/**
 * Parse VTT text into an array of cue objects (convenience wrapper).
 * @param {string} vtt - Raw VTT text
 * @returns {VTTCue[]} Array of parsed cues
 */
export function parseVTT(vtt) {
  return parseVttDocument(vtt).cues;
}

// Serialize a single cue block (id? / timestamp line + settings / text).
function serializeCue(cue) {
  let block = '';
  if (cue.id) block += cue.id + '\n';
  block +=
    formatTimestamp(cue.startTime) + ' --> ' + formatTimestamp(cue.endTime);
  if (cue.settings) block += ' ' + cue.settings;
  block += '\n' + (cue.text ?? '');
  return block;
}

/**
 * Serialize a VTT document back to text, re-interleaving opaque extras.
 * @param {VTTDocument} doc
 * @returns {string} VTT formatted text
 */
export function serializeVttDocument(doc) {
  const header =
    doc?.header && doc.header.trim()
      ? doc.header.replace(/\s+$/, '')
      : 'WEBVTT';
  const cues = doc?.cues || [];
  const extras = doc?.extras || [];

  if (cues.length === 0 && extras.length === 0) {
    return header + '\n\n';
  }

  const out = [header];
  const emitExtrasBefore = (i) => {
    for (const ex of extras) {
      if (ex.beforeIndex === i && ex.text)
        out.push(ex.text.replace(/\s+$/, ''));
    }
  };

  for (let i = 0; i < cues.length; i++) {
    emitExtrasBefore(i);
    out.push(serializeCue(cues[i]));
  }
  emitExtrasBefore(cues.length);

  return out.join('\n\n') + '\n';
}

/**
 * Serialize an array of cues back to VTT text (convenience wrapper).
 * @param {VTTCue[]} cues - Array of cue objects
 * @returns {string} VTT formatted text
 */
export function serializeVTT(cues) {
  return serializeVttDocument({ header: 'WEBVTT', cues, extras: [] });
}

/**
 * Minimal, conservative normalization applied before persisting/validating.
 * Strips BOM, trims line ends, collapses 3+ blank lines, ensures a WEBVTT
 * header and a single trailing newline. Never rewrites cue settings or inline
 * tags, so visual formatting passes through untouched.
 * @param {string} text
 * @returns {string}
 */
export function normalizeVtt(text) {
  if (!text) return 'WEBVTT\n';
  let n = text.replace(/^\uFEFF/, '');
  n = n
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    .join('\n');
  n = n.replace(/\n{3,}/g, '\n\n');
  if (!n.trimStart().startsWith('WEBVTT')) {
    n = 'WEBVTT\n\n' + n.trimStart();
  }
  n = n.replace(/\s+$/, '') + '\n';
  return n;
}

/**
 * Validate VTT for save. Self-contained and pure (no window dependency) so it
 * is unit-testable, deterministic, and safe under SSR. Produces clear,
 * cue-numbered messages; non-cue blocks (header/NOTE/STYLE) are ignored.
 *
 * Scope (deliberate): we require a WEBVTT header, well-formed in-range
 * timestamps, and end > start (a spec MUST). We intentionally do NOT flag two
 * things browsers accept: cue overlap (the spec explicitly allows it — e.g.
 * simultaneous speakers) and out-of-order start times (a spec MUST, but vtt.js
 * renders by time regardless of file order, so blocking would only annoy).
 *
 * Errors are returned as locale-free descriptors, not prose: this helper is
 * pure and has no intl. The caller formats `id` + `values` for display.
 * @param {string} vtt
 * @returns {{ valid: boolean, errors: Array<{id: string, values?: object}> }}
 */
export function validateVtt(vtt) {
  const errors = [];

  if (!vtt || typeof vtt !== 'string' || !vtt.trim()) {
    return { valid: false, errors: [{ id: 'empty' }] };
  }

  const clean = vtt.replace(/^\uFEFF/, '');
  if (!clean.trimStart().startsWith('WEBVTT')) {
    errors.push({ id: 'missingHeader' });
  }

  const blocks = clean.split(/\n\s*\n/);
  let cueNum = 0;
  let blockIdx = -1;
  for (const block of blocks) {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    if (lines.length === 0) continue;
    blockIdx += 1;

    const tsIdx = lines.findIndex((l) => l.includes('-->'));
    if (tsIdx === -1) {
      // A non-cue block is only legitimate as the header or a recognized
      // NOTE / STYLE / REGION block. Anything else is stray/garbage content
      // (e.g. random text pasted at the end) and must be surfaced.
      if (blockIdx === 0) continue; // header — checked above
      const head = lines[0].trim();
      if (!/^(NOTE|STYLE|REGION)(\s|$)/.test(head)) {
        errors.push({ id: 'strayContent', values: { content: head } });
      }
      continue;
    }

    cueNum += 1;
    const tsParts = lines[tsIdx].split('-->');
    if (tsParts.length !== 2) {
      errors.push({ id: 'malformedTimestampLine', values: { cue: cueNum } });
      continue;
    }

    const startStr = tsParts[0].trim();
    const endStr = tsParts[1].trim().split(/\s/)[0];
    if (!isValidTimestamp(startStr)) {
      errors.push({
        id: 'invalidStartTimestamp',
        values: { cue: cueNum, timestamp: startStr },
      });
    }
    if (!isValidTimestamp(endStr)) {
      errors.push({
        id: 'invalidEndTimestamp',
        values: { cue: cueNum, timestamp: endStr },
      });
    }
    if (
      isValidTimestamp(startStr) &&
      isValidTimestamp(endStr) &&
      parseTimestamp(endStr) <= parseTimestamp(startStr)
    ) {
      errors.push({ id: 'endBeforeStart', values: { cue: cueNum } });
    }
  }

  return { valid: errors.length === 0, errors };
}
